import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Bell, BellRinging, Warning, ShieldWarning, X,
  ArrowUpRight, Lifebuoy, CircleNotch, Check,
} from "@phosphor-icons/react";

const SEVERITY_THEME = {
  critical: { bg: "bg-rose-500/15",   text: "text-rose-400",   dot: "bg-rose-500",   ring: "ring-rose-500/20" },
  warning:  { bg: "bg-amber-400/15",  text: "text-amber-400",  dot: "bg-amber-400",  ring: "ring-amber-400/20" },
  info:     { bg: "bg-sky-500/15",    text: "text-sky-400",    dot: "bg-sky-400",    ring: "ring-sky-500/20" },
};

const EVENT_LABEL = {
  whatsapp_disconnected:  "WhatsApp desconectou",
  negative_margin:        "Margem ficou negativa",
  high_failure_rate_24h:  "Taxa de falhas alta (24h)",
  activity_drop:          "Queda de atividade",
  handoff_spike:          "Pico de handoffs humanos",
  tenant_suspended:       "Tenant suspenso",
  tenant_cancelled:       "Tenant cancelado",
  tenant_reactivated:     "Tenant reativado",
};

function eventLabel(code) {
  if (code.startsWith("level_drop:"))
    return `Saúde piorou (${code.split(":")[1]})`;
  if (code.startsWith("level_up:"))
    return `Saúde melhorou (${code.split(":")[1]})`;
  return EVENT_LABEL[code] || code;
}

function timeAgo(iso) {
  if (!iso) return "—";
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d atrás`;
}

export default function AdminAlertsBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/admin/alerts?limit=20");
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Click outside to close
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const counts = data?.counts || { critical: 0, warning: 0, info: 0 };
  const total = counts.critical + counts.warning + counts.info;
  const hasCritical = counts.critical > 0;
  const items = data?.items || [];

  const dismiss = async (id, e) => {
    e?.stopPropagation();
    try {
      await api.post(`/admin/alerts/${id}/dismiss`);
      load();
    } catch { toast.error("Falha ao dispensar alerta"); }
  };

  const dismissAll = async () => {
    try {
      const { data } = await api.post("/admin/alerts/dismiss-all");
      toast.success(`${data.dismissed} alertas dispensados`);
      load();
    } catch { toast.error("Falha ao dispensar alertas"); }
  };

  const openTenant = (a) => {
    setOpen(false);
    navigate(`/admin-master/tenants/${a.tenant_id}`);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="admin-alerts-bell"
        className="relative w-9 h-9 rounded-md flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
        aria-label="Alertas"
      >
        {hasCritical ? (
          <BellRinging size={16} weight="fill" className="text-amber-400 animate-[wiggle_1.2s_ease-in-out_infinite]" />
        ) : (
          <Bell size={16} weight="regular" />
        )}
        {total > 0 && (
          <span
            data-testid="admin-alerts-badge"
            className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center ${
              hasCritical ? "bg-rose-500 text-white" : "bg-amber-400 text-slate-900"
            }`}
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="admin-alerts-dropdown"
          className="absolute right-0 top-11 w-[420px] max-h-[560px] flex flex-col bg-[#0E0E11] border border-white/10 rounded-lg shadow-2xl z-50 overflow-hidden"
        >
          <header className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <div className="flex items-center gap-2">
              <ShieldWarning size={14} weight="regular" className="text-slate-400" />
              <div>
                <div className="font-heading text-[14px] font-semibold text-white">Central de alertas</div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mt-0.5">
                  {total === 0 ? "Sem alertas pendentes" :
                    `${counts.critical} crítico · ${counts.warning} atenção · ${counts.info} info`}
                </div>
              </div>
            </div>
            {total > 0 && (
              <button
                onClick={dismissAll}
                data-testid="admin-alerts-dismiss-all"
                className="text-[11px] text-slate-400 hover:text-white px-2 py-1 rounded transition-colors"
              >
                Limpar tudo
              </button>
            )}
          </header>

          <div className="overflow-y-auto flex-1">
            {loading && items.length === 0 && (
              <div className="p-8 text-center"><CircleNotch size={16} className="animate-spin text-slate-500 inline" /></div>
            )}
            {!loading && items.length === 0 && (
              <div className="p-8 text-center" data-testid="admin-alerts-empty">
                <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center mb-3">
                  <Check size={18} weight="bold" className="text-emerald-400" />
                </div>
                <div className="text-[13px] text-white font-semibold">Tudo sob controle</div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Nenhuma transição crítica detectada na varredura mais recente.
                </div>
              </div>
            )}
            <ul className="divide-y divide-white/5">
              {items.map((a) => {
                const t = SEVERITY_THEME[a.severity] || SEVERITY_THEME.info;
                return (
                  <li key={a.id} className="hover:bg-white/[0.03] transition-colors">
                    <div className="px-4 py-3">
                      <div className="flex items-start gap-2.5">
                        <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${t.bg}`}>
                          <Warning size={13} weight="fill" className={t.text} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[13px] font-semibold text-white truncate">{a.tenant_name}</span>
                            <span className={`w-1 h-1 rounded-full ${t.dot}`} />
                            <span className={`text-[10px] uppercase tracking-wider ${t.text} font-semibold`}>{a.severity}</span>
                          </div>
                          <ul className="mt-1 space-y-0.5">
                            {(a.event_codes || []).slice(0, 3).map((c) => (
                              <li key={c} className="text-[11px] text-slate-300 leading-snug">
                                · {eventLabel(c)}
                              </li>
                            ))}
                          </ul>
                          {a.score_to != null && (
                            <div className="text-[10px] text-slate-500 mt-1.5">
                              Score: {a.score_from ?? "—"} → <strong className="text-slate-300 tabular-nums">{a.score_to}</strong>
                              {" · "}{timeAgo(a.created_at)}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={(e) => dismiss(a.id, e)}
                          data-testid={`alert-dismiss-${a.id}`}
                          className="text-slate-600 hover:text-white p-1"
                          title="Dispensar"
                        >
                          <X size={11} weight="regular" />
                        </button>
                      </div>
                      <div className="flex items-center gap-1.5 mt-2 ml-9">
                        <button
                          onClick={() => openTenant(a)}
                          data-testid={`alert-open-tenant-${a.id}`}
                          className="inline-flex items-center gap-1 text-[11px] bg-white/5 hover:bg-white/10 text-white px-2 py-1 rounded transition-colors"
                        >
                          Abrir tenant <ArrowUpRight size={10} weight="regular" />
                        </button>
                        <a
                          href={`mailto:suporte@agentus.com.br?subject=Tenant cr%C3%ADtico%3A%20${encodeURIComponent(a.tenant_name)}&body=Tenant%20%5B${a.tenant_id}%5D%20detectado%20em%20${encodeURIComponent(a.severity)}%20%E2%80%94%20eventos%3A%20${encodeURIComponent((a.event_codes || []).join(", "))}`}
                          data-testid={`alert-support-${a.id}`}
                          className="inline-flex items-center gap-1 text-[11px] bg-white/5 hover:bg-white/10 text-slate-300 px-2 py-1 rounded transition-colors"
                        >
                          <Lifebuoy size={10} weight="regular" /> Suporte
                        </a>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <footer className="px-4 py-2.5 border-t border-white/5 flex items-center justify-between text-[10px] text-slate-500">
            <span>Verificação a cada 30 min</span>
            <button
              onClick={async () => { setLoading(true); await api.post("/admin/alerts/scan"); load(); }}
              className="hover:text-white transition-colors"
              data-testid="admin-alerts-scan-now"
            >
              Verificar agora
            </button>
          </footer>
        </div>
      )}

      <style>{`
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-8deg); }
          75% { transform: rotate(8deg); }
        }
      `}</style>
    </div>
  );
}
